import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Home, Compass, TrendingUp, Users, MapPin, Mail } from "lucide-react";
import { FullLogo } from "./Logo";
import { LiquidToggle } from "./ui/liquid-toggle";
import { ThemeToggle } from "./ThemeToggle";
import { useTheme } from "@/lib/theme";
import { useLang, useT } from "@/lib/i18n";
import { NAV } from "../constants/testIds";

// Brand gradients (teal / seafoam / coral) per nav item.
const LINKS = [
  { to: "/", key: "home", icon: Home, testId: NAV.linkHome, g: ["#0D5F5E", "#4DB6AC"] },
  { to: "/about", key: "about", icon: Compass, testId: NAV.linkAbout, g: ["#0B4F4E", "#BEE5E0"] },
  { to: "/markets", key: "markets", icon: TrendingUp, testId: NAV.linkMarkets, g: ["#0D5F5E", "#FF6857"] },
  { to: "/partners", key: "partners", icon: Users, testId: NAV.linkPartners, g: ["#0E5E5C", "#4DB6AC"] },
  { to: "/hubs", key: "hubs", icon: MapPin, testId: NAV.linkHubs, g: ["#0D5F5E", "#2F8A80"] },
  { to: "/contact", key: "contact", icon: Mail, testId: NAV.linkContact, g: ["#0B4F4E", "#FF7D6D"] },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { lang, setLang } = useLang();
  const t = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const onHero = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const overTeal = onHero && !scrolled;
  const showFrosted = !onHero || scrolled;
  const textColor = overTeal ? "#F7F6F2" : "var(--nexxis-ink)";
  const subColor = overTeal ? "rgba(247,246,242,0.7)" : "var(--nexxis-ink-muted)";

  const isActiveLink = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname === to || location.pathname.startsWith(to + "/");
  const inactiveText = overTeal ? "rgba(247,246,242,0.82)" : "var(--nexxis-ink-muted)";
  const frostedBg = theme === "dark" ? "rgba(10,38,37,0.78)" : "rgba(247,246,242,0.78)";
  const frostedBorder = theme === "dark" ? "rgba(190,229,224,0.18)" : "rgba(13,95,94,0.18)";

  return (
    <>
      <header
        data-testid={NAV.root}
        className="fixed inset-x-0 top-0 z-50"
        style={{
          transition: "background-color 300ms ease, border-color 300ms ease, backdrop-filter 300ms ease",
          backgroundColor: showFrosted ? frostedBg : "transparent",
          backdropFilter: showFrosted ? "saturate(140%) blur(14px)" : "none",
          WebkitBackdropFilter: showFrosted ? "saturate(140%) blur(14px)" : "none",
          borderBottom: showFrosted ? `1px solid ${frostedBorder}` : "1px solid transparent",
        }}
      >
        <div className="mx-auto flex h-[68px] max-w-[1280px] items-center justify-between px-5 sm:px-8">
          <button
            onClick={() => navigate("/")}
            data-testid={NAV.logo}
            className="flex items-center gap-2.5"
            aria-label="NEXXIS — Home"
            style={{ color: textColor }}
          >
            <FullLogo height={34} light={overTeal} />
          </button>

          {/* Desktop: icon + label tabs with a brand-gradient pill that slides (liquid) */}
          <nav className="hidden lg:flex items-center gap-1">
            {LINKS.map((l) => {
              const Icon = l.icon;
              const active = isActiveLink(l.to);
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={l.testId}
                  data-active={active}
                  className="nxx-gradtab relative inline-flex items-center gap-2 rounded-full px-3.5 py-2 font-ui text-[13px] tracking-[0.02em]"
                  style={{ "--tab-color": inactiveText }}
                >
                  {active && (
                    <motion.span
                      layoutId="nav-pill"
                      aria-hidden="true"
                      className="absolute inset-0 rounded-full"
                      style={{ background: `linear-gradient(45deg, ${l.g[0]}, ${l.g[1]})` }}
                      transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    />
                  )}
                  <span className="relative z-10 inline-flex items-center gap-2">
                    <Icon size={15} strokeWidth={1.9} />
                    {t.nav[l.key]}
                  </span>
                </NavLink>
              );
            })}
          </nav>

          {/* Right controls (desktop) */}
          <div className="flex items-center gap-3">
            <ThemeToggle overTeal={overTeal} className="hidden lg:flex" />

            <div
              data-testid={NAV.langToggle}
              className="hidden lg:flex items-center gap-2"
              aria-label="Language"
              title={lang === "en" ? "Switch to Español" : "Switch to English"}
            >
              <span
                className="font-ui text-[11px]"
                style={{ color: lang === "en" ? (overTeal ? "#F7F6F2" : "var(--nexxis-ink)") : subColor, letterSpacing: "0.18em", fontWeight: 600 }}
              >
                EN
              </span>
              <LiquidToggle
                checked={lang === "es"}
                onCheckedChange={(c) => setLang(c ? "es" : "en")}
                variant={overTeal ? "inverse" : "default"}
                size={28}
              />
              <span
                className="font-ui text-[11px]"
                style={{ color: lang === "es" ? (overTeal ? "#F7F6F2" : "var(--nexxis-ink)") : subColor, letterSpacing: "0.18em", fontWeight: 600 }}
              >
                ES
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* === Mobile: fluid drip menu === */}
      <button
        data-testid={NAV.mobileOpen}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        className="fixed right-4 top-3.5 z-[80] inline-flex h-11 w-11 items-center justify-center rounded-full lg:hidden"
        style={{
          background: "linear-gradient(45deg, #0D5F5E, #4DB6AC)",
          color: "#fff",
          boxShadow: "0 8px 22px rgba(13,95,94,0.4)",
        }}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.span
            key={open ? "x" : "menu"}
            initial={{ opacity: 0, rotate: -90, scale: 0.6 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 90, scale: 0.6 }}
            transition={{ duration: 0.2 }}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </motion.span>
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="bd"
              className="fixed inset-0 z-[60] lg:hidden"
              style={{ background: "rgba(6,32,31,0.55)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <div
              data-testid={NAV.mobileSheet}
              className="fixed right-4 top-[72px] z-[70] flex flex-col items-end gap-3 lg:hidden"
            >
              {LINKS.map((l, i) => {
                const active = isActiveLink(l.to);
                return (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, y: -14, scale: 0.8 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.8, transition: { delay: (LINKS.length - i) * 0.03 } }}
                    transition={{ delay: i * 0.05, type: "spring", stiffness: 420, damping: 28 }}
                  >
                    <NavLink
                      to={l.to}
                      end={l.to === "/"}
                      data-testid={`${l.testId}-mobile`}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-2.5 rounded-full py-3 pl-5 pr-5 font-ui text-[14.5px]"
                      style={
                        active
                          ? {
                              background: `linear-gradient(45deg, ${l.g[0]}, ${l.g[1]})`,
                              color: "#fff",
                              boxShadow: "0 8px 20px rgba(13,95,94,0.35)",
                            }
                          : {
                              background: "rgba(11,79,78,0.85)",
                              color: "#F7F6F2",
                              border: "1px solid rgba(190,229,224,0.18)",
                              backdropFilter: "blur(8px)",
                              WebkitBackdropFilter: "blur(8px)",
                            }
                      }
                    >
                      <l.icon size={18} strokeWidth={1.9} style={{ color: active ? "#fff" : "#BEE5E0" }} />
                      <span>{t.nav[l.key]}</span>
                    </NavLink>
                  </motion.div>
                );
              })}

              {/* toggles + contact */}
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.85 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.85 }}
                transition={{ delay: LINKS.length * 0.05, type: "spring", stiffness: 420, damping: 28 }}
                className="mt-1 flex items-center gap-4 rounded-full px-4 py-2"
                style={{ background: "rgba(11,79,78,0.82)", border: "1px solid rgba(190,229,224,0.18)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
              >
                <ThemeToggle overTeal />
                <div className="flex items-center gap-2">
                  <span className="font-ui text-[11px]" style={{ color: "#F7F6F2", letterSpacing: "0.18em" }}>EN</span>
                  <LiquidToggle checked={lang === "es"} onCheckedChange={(c) => setLang(c ? "es" : "en")} variant="inverse" size={26} />
                  <span className="font-ui text-[11px]" style={{ color: "#F7F6F2", letterSpacing: "0.18em" }}>ES</span>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
